import { useEffect } from "react";

// Reveals any element carrying a `data-reveal` attribute as it scrolls into
// view by toggling the `is-visible` class (styled in global.css). A single
// IntersectionObserver handles the whole page; a MutationObserver picks up
// nodes added later by lazy-loaded routes.
export default function useScrollReveal() {
  useEffect(() => {
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // If the browser lacks IntersectionObserver or the user opts out of
    // motion, just show everything.
    if (prefersReduced || typeof IntersectionObserver === "undefined") {
      const showAll = () =>
        document
          .querySelectorAll("[data-reveal]:not(.is-visible)")
          .forEach((el) => el.classList.add("is-visible", "reveal-done"));
      showAll();
      // Lazy-loaded routes mount after this effect runs; reveal them too.
      const mo = new MutationObserver(showAll);
      mo.observe(document.body, { childList: true, subtree: true });
      return () => mo.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
            // After the entrance transition (0.7s + up to 0.4s stagger
            // delay), mark it done so global.css can clear the leftover
            // translate/transform, which otherwise pins the element in its
            // own stacking context and breaks hit-testing.
            setTimeout(() => entry.target.classList.add("reveal-done"), 1200);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    const observeAll = () =>
      document
        .querySelectorAll("[data-reveal]:not(.is-visible)")
        .forEach((el) => observer.observe(el));

    observeAll();

    // Catch elements mounted after the first paint (e.g. lazy routes).
    const mutationObserver = new MutationObserver(observeAll);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}
